/**
 * Commander Design System - Admin Dashboard Theme
 *
 * Modern SaaS dashboard aesthetic with light/dark theme support.
 * Clean, minimal design with subtle shadows and smooth transitions.
 * Uses Inter font family throughout.
 */

import { type MantineThemeOverride, mergeThemeOverrides, rem } from '@mantine/core';
import { sharedTheme } from './shared';

/**
 * Light theme colour palette
 * Clean, professional SaaS aesthetic
 */
const lightPalette = {
  /** Light grey background */
  background: '#F8FAFC',
  /** White card surfaces */
  surface: '#FFFFFF',
  /** Alternate surface for hover */
  surfaceAlt: '#F1F5F9',
  /** Card background */
  card: '#FFFFFF',
  /** Border colour */
  border: '#E2E8F0',
  /** Hover state */
  hover: '#F1F5F9',
  /** Primary text */
  text: '#1E293B',
  /** Secondary text */
  textSecondary: '#64748B',
  /** Muted/disabled text */
  textMuted: '#94A3B8',
};

/**
 * Dark theme colour palette
 * Professional dark mode for reduced eye strain
 */
const darkPalette = {
  /** Dark navy background */
  background: '#0F172A',
  /** Dark surface */
  surface: '#1E293B',
  /** Alternate dark surface */
  surfaceAlt: '#334155',
  /** Card background */
  card: '#1E293B',
  /** Border colour */
  border: '#334155',
  /** Hover state */
  hover: '#334155',
  /** Primary text */
  text: '#F1F5F9',
  /** Secondary text */
  textSecondary: '#94A3B8',
  /** Muted/disabled text */
  textMuted: '#64748B',
};

/**
 * Status/accent colours - consistent across themes
 */
const accentColours = {
  /** Blue - Active sessions, primary actions */
  blue: '#3B82F6',
  blueBg: '#EFF6FF',
  blueBgDark: 'rgba(59, 130, 246, 0.15)',

  /** Orange - Infrastructure, warnings */
  orange: '#F59E0B',
  orangeBg: '#FFFBEB',
  orangeBgDark: 'rgba(245, 158, 11, 0.15)',

  /** Red - Errors, overdue items */
  red: '#EF4444',
  redBg: '#FEF2F2',
  redBgDark: 'rgba(239, 68, 68, 0.15)',

  /** Green - Online, success */
  green: '#22C55E',
  greenBg: '#F0FDF4',
  greenBgDark: 'rgba(34, 197, 94, 0.15)',

  /** Amber - Stunned, attention needed */
  amber: '#F59E0B',
  amberBg: '#FFFBEB',
  amberBgDark: 'rgba(245, 158, 11, 0.15)',
};

/**
 * Inter font family configuration
 */
const fontFamily = {
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
};

/**
 * Shadow definitions - clean, subtle shadows
 */
const shadows = {
  card: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  cardHover: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
  cardDark: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
  cardHoverDark: '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
};

/**
 * Admin-specific theme overrides
 */
const adminOverrides: MantineThemeOverride = {
  /** Use Inter font for all text */
  fontFamily: fontFamily.sans,
  fontFamilyMonospace: fontFamily.mono,

  /** Headings configuration */
  headings: {
    fontFamily: fontFamily.sans,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(30), lineHeight: '1.2' },
      h2: { fontSize: rem(24), lineHeight: '1.25' },
      h3: { fontSize: rem(20), lineHeight: '1.3' },
      h4: { fontSize: rem(16), lineHeight: '1.35' },
    },
  },

  /** Expose palettes and colours for components */
  other: {
    lightPalette,
    darkPalette,
    accentColours,
    shadows,
    fontFamily,
  },

  /** Component-specific overrides */
  components: {
    /** AppShell - Uses CSS variables for theme switching */
    AppShell: {
      styles: {
        root: {
          '--app-shell-border-color': 'var(--border-colour)',
        },
        main: {
          backgroundColor: 'var(--bg-base)',
          transition: 'background-color 200ms ease, padding-left 200ms ease',
        },
        header: {
          backgroundColor: 'var(--bg-card)',
          borderBottom: '1px solid var(--border-colour)',
          transition: 'background-color 200ms ease, border-color 200ms ease',
        },
        navbar: {
          backgroundColor: 'var(--bg-card)',
          borderRight: '1px solid var(--border-colour)',
          transition: 'background-color 200ms ease, border-color 200ms ease',
        },
      },
    },

    /** NavLink - Clean, minimal active state */
    NavLink: {
      styles: {
        root: {
          borderRadius: 'var(--mantine-radius-md)',
          color: 'var(--text-secondary)',
          transition: 'all 150ms ease',
          '&:hover': {
            backgroundColor: 'var(--bg-base)',
            color: 'var(--text-primary)',
          },
          '&[data-active]': {
            backgroundColor: 'var(--blue-bg)',
            color: accentColours.blue,
            fontWeight: 500,
          },
        },
      },
    },

    /** Card - White with subtle shadow */
    Card: {
      styles: {
        root: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
          boxShadow: 'var(--shadow)',
          transition: 'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        },
      },
    },

    /** Paper - Consistent surface */
    Paper: {
      styles: {
        root: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
          transition: 'background-color 200ms ease, border-color 200ms ease',
        },
      },
    },

    /** Table - Clean, minimal styling */
    Table: {
      defaultProps: {
        striped: false,
        highlightOnHover: true,
        withTableBorder: false,
        withColumnBorders: false,
      },
      styles: {
        table: {
          '--table-hover-color': 'var(--bg-base)',
        },
        th: {
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          fontSize: rem(11),
          fontWeight: 600,
          letterSpacing: '0.05em',
          borderBottom: '1px solid var(--border-colour)',
          padding: '12px 16px',
        },
        td: {
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--border-colour)',
          padding: '12px 16px',
          fontSize: rem(14),
        },
        tr: {
          transition: 'background-color 150ms ease',
        },
      },
    },

    /** Button - Clean, professional styling */
    Button: {
      styles: {
        root: {
          fontWeight: 500,
          transition: 'all 150ms ease',
        },
      },
    },

    /** TextInput - Clean input fields */
    TextInput: {
      styles: {
        input: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
          color: 'var(--text-primary)',
          transition: 'all 150ms ease',
          '&:focus': {
            borderColor: accentColours.blue,
            boxShadow: `0 0 0 1px ${accentColours.blue}`,
          },
          '&::placeholder': {
            color: 'var(--text-muted)',
          },
        },
      },
    },

    /** Select - Clean dropdown */
    Select: {
      styles: {
        input: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
          color: 'var(--text-primary)',
        },
        dropdown: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
        },
      },
    },

    /** Tabs - Underline style */
    Tabs: {
      styles: {
        tab: {
          color: 'var(--text-secondary)',
          fontWeight: 500,
          transition: 'all 150ms ease',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
          },
          '&[data-active]': {
            color: accentColours.blue,
            borderColor: accentColours.blue,
          },
        },
      },
    },

    /** Badge - Clean status indicators */
    Badge: {
      styles: {
        root: {
          textTransform: 'none',
          fontSize: rem(12),
          fontWeight: 500,
        },
      },
    },

    /** Title - Uses theme text colour */
    Title: {
      styles: {
        root: {
          color: 'var(--text-primary)',
        },
      },
    },

    /** Text - Uses theme text colour */
    Text: {
      styles: {
        root: {
          color: 'var(--text-primary)',
        },
      },
    },

    /** Modal - Theme-aware */
    Modal: {
      styles: {
        content: {
          backgroundColor: 'var(--bg-card)',
        },
        header: {
          backgroundColor: 'var(--bg-card)',
        },
      },
    },

    /** Notification - Theme-aware */
    Notification: {
      styles: {
        root: {
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-colour)',
        },
      },
    },

    /** ActionIcon - Clean icon buttons */
    ActionIcon: {
      styles: {
        root: {
          transition: 'all 150ms ease',
        },
      },
    },
  },
};

/**
 * Complete admin theme - Modern SaaS dashboard aesthetic
 * Supports light and dark modes via CSS variables
 */
export const adminTheme = mergeThemeOverrides(sharedTheme, adminOverrides);

/**
 * Export palettes for direct use in components
 */
export { lightPalette, darkPalette, accentColours, shadows };
